/**
 * UplinkBar — the fixed bottom communication bar.
 *
 * In v2 this bar rendered but did nothing: it had no submit handler and no
 * backend call. v3 wires it to the same command processor as the terminal, so
 * `say hello` here really posts to Telegram, and replies routed back to this
 * visitor surface as a toast above the bar.
 *
 * It behaves like a chat box, not a shell:
 *  - plain text is transmitted (the `say ` prefix is added for the visitor);
 *  - known commands (`help`, `now`, `moony`, …) still work;
 *  - every line is mirrored into the main terminal transcript, with the same
 *    optimistic pending line, so both windows stay one conversation;
 *  - `name <you>` sets the shared identity and boots the terminal.
 *
 * It is pinned to the viewport (not inside the snap sections) and each section
 * reserves bottom padding for it, so it can never push content off-screen or
 * trap the scroll position.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Moon, Send } from 'lucide-react';
import { useComms } from '@/lib/CommsContext';
import { useRadio } from '@/lib/RadioContext';
import { executeCommand, formatIncomingReply, normalizeUplinkInput } from '@/lib/commands';
import { WHOAMI_FALLBACK_RESPONSES } from '@/lib/verses';

const TOAST_TTL = 9000;

export default function UplinkBar({ onOpenMoony, unreadReplies }) {
  const {
    say,
    recordCommand,
    startSession,
    session,
    relayStatus,
    isSending,
    subscribe,
    markRepliesRead,
    getTerminal,
  } = useComms();
  const radio = useRadio();

  const [draft, setDraft] = useState('');
  const [toasts, setToasts] = useState([]);
  const inputRef = useRef(null);
  const timers = useRef(new Set());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      timers.current.forEach(clearTimeout);
      timers.current.clear();
    };
  }, []);

  const pushToast = useCallback((toast) => {
    setToasts((prev) => [...prev.slice(-2), toast]);
    const timer = setTimeout(
      () => setToasts((prev) => prev.filter((item) => item.id !== toast.id)),
      TOAST_TTL,
    );
    timers.current.add(timer);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Inbound Telegram replies also surface here, so the visitor sees them no
  // matter which section they are scrolled to. The terminal keeps the
  // permanent copy in its transcript. Neither shows a raw Telegram id.
  useEffect(
    () =>
      subscribe((reply) => {
        const { content, meta } = formatIncomingReply(reply);
        pushToast({
          id: `tg-${reply.id ?? Date.now()}`,
          type: 'reply',
          text: `${content}\n${meta}`,
        });
      }),
    [pushToast, subscribe],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const typed = draft.trim();
    if (!typed || isSending) return;

    setDraft('');

    // Chat-box behaviour: anything that is not a known command is transmitted.
    const line = normalizeUplinkInput(typed);

    const terminal = getTerminal();
    // Mirror the line into the main terminal with its optimistic placeholder.
    const token = terminal ? terminal.beginPending(line) : null;

    const results = await executeCommand(line, {
      username: session.username,
      whoami: WHOAMI_FALLBACK_RESPONSES,
      say,
      setName: startSession,
      radio,
      relayStatus,
      openMoony: onOpenMoony,
    });

    recordCommand(line);

    if (results.some((result) => result.type === 'clear')) {
      terminal?.clear();
      setToasts([]);
      return;
    }

    const output = results.filter((result) => result.type !== 'command');

    if (terminal && token) {
      terminal.resolvePending(token, output);
    } else if (mounted.current) {
      // Terminal not mounted (never happens today, but stay honest).
      terminal?.append(output);
    }

    output.forEach((result) => pushToast({
      id: `${Date.now()}-${Math.random()}`,
      type: result.type,
      text: result.content,
    }));
  };

  const ledClass = relayStatus.enabled
    ? relayStatus.pollerRunning
      ? ''
      : 'dmr-led--busy'
    : 'dmr-led--idle';

  return (
    <>
      {/* Inbound replies + command output surface just above the bar. */}
      <div
        className="pointer-events-none fixed bottom-16 left-0 right-0 z-[var(--z-toast)] flex flex-col items-center gap-2 px-4"
        aria-live="polite"
      >
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.button
              key={toast.id}
              type="button"
              onClick={() => dismissToast(toast.id)}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className={`pointer-events-auto max-w-md rounded border px-3 py-2 text-left text-xs shadow-lg backdrop-blur-md ${
                toast.type === 'error'
                  ? 'border-[#ff5f56]/40 bg-[#1a0d0d]/90 text-[#ff9b93]'
                  : 'border-[color:var(--phosphor-20)] bg-[#0d0d0d]/95 text-[color:var(--phosphor)]'
              }`}
            >
              <span className="line-clamp-3 whitespace-pre-wrap">{toast.text}</span>
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      <div className="dmr-uplink" data-testid="uplink-bar">
        <span className="dmr-uplink__status" aria-hidden="true">
          <span className={`dmr-led ${ledClass}`} />
          uplink
          <AnimatePresence>
            {unreadReplies > 0 && (
              <motion.span
                key="unread"
                className="dmr-badge"
                style={{ position: 'static' }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                data-testid="uplink-unread"
              >
                {unreadReplies > 9 ? '9+' : unreadReplies}
              </motion.span>
            )}
          </AnimatePresence>
        </span>

        <form className="dmr-uplink__well" onSubmit={handleSubmit} data-testid="uplink-form">
          <span className="dmr-uplink__prompt" aria-hidden="true">
            &gt;
          </span>
          <input
            ref={inputRef}
            className="dmr-input"
            type="text"
            value={draft}
            maxLength={500}
            autoComplete="off"
            spellCheck={false}
            placeholder={
              session.username
                ? `say something, ${session.username}...`
                : 'type "name <you>" to identify yourself, then say something...'
            }
            onChange={(event) => setDraft(event.target.value)}
            onFocus={markRepliesRead}
            data-testid="uplink-input"
            aria-label="Message to transmit"
          />
          <button
            type="submit"
            className="dmr-button shrink-0 !px-2.5 !py-1.5"
            disabled={isSending || !draft.trim()}
            aria-label="Transmit message"
            data-testid="uplink-send"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </form>

        <div className="relative shrink-0">
          <button
            type="button"
            onClick={onOpenMoony}
            className="dmr-button dmr-button--ghost !px-2.5 !py-1.5"
            aria-label="Open chat with Moony"
            data-testid="moony-launcher"
          >
            <Moon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}
